import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, RotateCcw } from "lucide-react";
import { api } from "../../lib/api";

/* ---------------- Users ---------------- */
export default function UsersTab() {
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState({});

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const setDepositPct = async (u, pct) => {
    setBusy((b) => ({ ...b, [u.user_id]: true }));
    try {
      const body = pct === null ? { deposit_percent: null } : { deposit_percent: Number(pct) };
      await api.patch(`/admin/users/${u.user_id}/deposit-percent`, body);
      toast.success(pct === null ? "Reset to default (50%)" : `Set to ${Math.round(pct * 100)}%`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy((b) => ({ ...b, [u.user_id]: false })); }
  };

  return (
    <div className="glass rounded-3xl overflow-hidden">
      <div className="px-5 py-4 border-b border-white/8">
        <div className="label-caps text-burgundy text-[11px]">Deposit % override · VIPs / repeat clients</div>
        <p className="text-white/50 text-xs mt-1">
          Default deposit is 50% of the ride total. Enter a value 0-100 to override per customer,
          or click <RotateCcw size={11} className="inline" /> to reset.
        </p>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-ink-800 text-white/50">
          <tr>
            <th className="text-left px-5 py-3 label-caps">Name</th>
            <th className="text-left px-5 py-3 label-caps">Email</th>
            <th className="text-left px-5 py-3 label-caps">Role</th>
            <th className="text-left px-5 py-3 label-caps">License</th>
            <th className="text-left px-5 py-3 label-caps">Deposit %</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <UserRow key={u.user_id} user={u} onSave={setDepositPct} busy={!!busy[u.user_id]} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function UserRow({ user, onSave, busy }) {
  const initialPct = user.deposit_percent != null ? Math.round(user.deposit_percent * 100) : "";
  const [pct, setPct] = useState(initialPct);

  useEffect(() => { setPct(initialPct); }, [initialPct]);
  const dirty = String(pct) !== String(initialPct);

  const save = () => {
    if (pct === "" || pct === null) return onSave(user, null);
    const n = Number(pct);
    if (Number.isNaN(n) || n < 0 || n > 100) {
      toast.error("Enter a number between 0 and 100");
      return;
    }
    onSave(user, n / 100);
  };

  return (
    <tr className="border-t border-white/5">
      <td className="px-5 py-3">{user.name}</td>
      <td className="px-5 py-3 text-white/70">{user.email}</td>
      <td className="px-5 py-3">
        <span className={user.role === "staff" ? "text-burgundy" : "text-white/50"}>{user.role || "customer"}</span>
      </td>
      <td className="px-5 py-3">{user.license_file_id ? <span className="text-platinum">on file</span> : <span className="text-white/40">no</span>}</td>
      <td className="px-5 py-3">
        <div className="inline-flex items-center gap-2">
          <input
            type="number"
            min="0"
            max="100"
            value={pct}
            placeholder="50"
            onChange={(e) => setPct(e.target.value)}
            data-testid={`bo-user-deposit-${user.user_id}`}
            className="w-16 bg-ink-800 border border-white/10 rounded-lg px-2 py-1 text-sm text-white outline-none focus:border-lime-400/50"
          />
          <span className="text-white/40 text-xs">%</span>
          {dirty && (
            <button
              type="button"
              onClick={save}
              disabled={busy}
              data-testid={`bo-user-deposit-save-${user.user_id}`}
              className="p-1.5 rounded-full bg-lime-400 text-black hover:bg-lime-300 disabled:opacity-50"
              title="Save"
            >
              <Save size={12} />
            </button>
          )}
          {user.deposit_percent != null && !dirty && (
            <button
              type="button"
              onClick={() => onSave(user, null)}
              disabled={busy}
              data-testid={`bo-user-deposit-reset-${user.user_id}`}
              className="p-1.5 rounded-full border border-white/15 text-white/60 hover:text-white hover:bg-white/10"
              title="Reset to default (50%)"
            >
              <RotateCcw size={12} />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
